/**
 * Component for our Graph on a combination of
 * Refunds + Store Credit + Exchanges.
 *
 * @component
 */

import { Area } from '@ant-design/plots';
import {
  Col,
  DatePicker,
  Divider,
  message,
  Row,
  Spin,
  Typography,
  Menu,
  Dropdown,
  Button,
} from 'antd';
import axios from 'axios';
import dayjs from 'dayjs';
import React from 'react';
import { Parser } from 'json2csv';
import { CSVLink } from 'react-csv';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import Upgrade from '../../../../../components/Upgrade';
import { SubscriptionContext } from '../../../../../contexts/SubscriptionContext';

const { RangePicker } = DatePicker;

const { Text } = Typography;

const exportHeaders = [
  {
    label: 'Volume',
    value: 'volume',
    key: 'volume',
  },
  {
    label: 'Date',
    value: 'date',
    key: 'date',
  },
  {
    label: 'Type',
    value: 'type',
    key: 'type',
  },
];

class Overview extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: false,
      date: undefined,
      data: [],
      exportLoading: false,
      dataCSVToBeExported: [],
      pdfLoading: false,
      timestamp: Date.now(),
    };
    this.printRef = React.createRef();
    this.csvLinkRef = React.createRef();
  }

  static contextType = SubscriptionContext;

  componentDidMount() {
    this.handleSubmit();
  }

  handleExportPdf = async () => {
    this.setState({ pdfLoading: true });

    try {
      if (this.props.startDate == null || this.props.endDate == null) {
        this.setState({ pdfLoading: false });
        return message.error('Select a start and end date first.', 4);
      }

      if (this.state.data.filter((item) => item.volume !== 0).length === 0) {
        this.setState({ pdfLoading: false });
        return message.error(
          'There is no data to export for the selected date range.',
          4
        );
      }

      const element = this.printRef.current;
      if (!element) {
        this.setState({ pdfLoading: false });
        return message.error('No content to export as PDF');
      }
      const originalStyle = element.style.cssText;
      element.style.cssText = `
        ${originalStyle} 
        padding: 10px;
      `;
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
      });
      element.style.cssText = originalStyle;
      const data = canvas.toDataURL('image/png');

      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'px',
        format: 'a4',
      });

      const imgProperties = pdf.getImageProperties(data);
      const pdfWidth = pdf.internal.pageSize.getWidth() - 10;
      const pdfHeight = (imgProperties.height * pdfWidth) / imgProperties.width;

      pdf.addImage(data, 'PNG', 0, 20, pdfWidth, pdfHeight);
      pdf.save(`RichReturns-Trend-Overview-${this.state.timestamp}.pdf`);

      message.success('PDF downloaded successfully');
    } catch (error) {
      this.setState({
        pdfLoading: false,
      });
      return message.error('Failed to generate PDF');
    } finally {
      this.setState({ pdfLoading: false });
    }
  };

  async handleExportCsv() {
    this.setState({
      exportLoading: true,
    });

    if (this.props.startDate == null || this.props.endDate == null) {
      this.setState({
        exportLoading: false,
      });
      return message.error('Select a start and end date first.', 4);
    }

    try {
      const response = await axios.post(
        '/api/analytics/compare-overview-by-date?',
        {
          startDate: this.props.startDate,
          endDate: this.props.endDate,
          isExport: true,
        }
      );

      if (response.data.error) {
        this.setState({
          exportLoading: false,
        });
        return message.error('Error creating export file of the Trend.', 4);
      }

      if (response.data.emailExport) {
        this.setState({
          exportLoading: false,
        });
        return message.success(
          'Trend is being generated. You will receive an email shortly with the download link.',
          4
        );
      }

      const dataParser = new Parser({
        fields: exportHeaders,
        quote: '',
      });
      response.data.data = response.data.data.filter(
        (item) => item.volume !== 0
      );

      if (response.data.data.length === 0) {
        this.setState({
          exportLoading: false,
        });
        return message.error(
          'There is no data to export for the selected date range.',
          4
        );
      }
      const dataCSVToBeExported = dataParser.parse(response.data.data);

      this.setState(
        {
          exportLoading: false,
          dataCSVToBeExported,
        },
        () => {
          if (
            this.csvLinkRef.current &&
            this.state.dataCSVToBeExported.length > 0
          ) {
            this.csvLinkRef.current.link.click();
            message.success('CSV downloaded successfully!', 2);
            this.handleResetDataToBeExported();
          }
        }
      );
    } catch (err) {
      this.setState({
        exportLoading: false,
      });
      return message.error('Error creating report csv.', err);
    }
  }
  handleResetDataToBeExported = () => {
    this.setState({
      dataCSVToBeExported: [],
    });
  };
  async handleSubmit() {
    this.setState({
      loading: true,
    });

    if (!this.props.startDate || !this.props.endDate) {
      this.setState({
        loading: false,
      });
      return;
    }
    try {
      const response = await axios.post(
        '/api/analytics/compare-overview-by-date?',
        {
          startDate: this.props.startDate,
          endDate: this.props.endDate,
        }
      );

      if (response.data.error) {
        return message.error('Error fetching analytics data from server.', 4);
      }

      return this.setState({
        data: response.data.data,
        loading: false,
      });
    } catch (err) {
      message.error('Error fetching analytics data.', 4);
    }
  }
  onChange(dates, dateStrings) {
    // syntax: function(dates: [dayjs, dayjs], dateStrings: [string, string])
    this.props.updateDate(dateStrings);
    this.setState(
      {
        checkboxDisabled: false,
      },
      () => {
        this.handleSubmit();
      }
    );
  }

  render() {
    const { date } = this.state;
    const disabledDate = (current) => {
      if (!date) {
        return false;
      }
      const tooLate = date && current.diff(date, 'days') > 45;
      const tooEarly = date && date.diff(current, 'days') > 45;
      return tooEarly || tooLate;
    };
    return (
      <React.Fragment>
        {/*
        <Row
          type="flex"
          justify="start"
          align="top"
          style={{ paddingBottom: 35 }}
        >
          <Col>
            
             <h1 className="TextHeading TextHeading--sm">
              <Tooltip title="Shows a comparison for the number of Exchange, Refund and Store-Credit items requested per day.">
                <Icon
                  style={{ fontSize: "16px", alignItems: "center" }}
                  type="info-circle"
                />
              </Tooltip>{" "}
              Overview
            </h1> 
          </Col>
        </Row>
        */}
        {this.context.featureAnalyticsAdvanced ? (
          <React.Fragment>
            <Row
              type="flex"
              justify="space-between"
              align="middle"
              style={{ marginBottom: '20px' }}
              gutter={16}
            >
              <Col
                xs={{ span: 24 }}
                sm={{ span: 8 }}
                md={{ span: 10 }}
                lg={{ span: 12 }}
              >
                <Text style={{ fontSize: 14 }}>
                  Shows a comparison for the number of Exchange, Refund and
                  Store-Credit items requested per day.
                </Text>
              </Col>
              <Col
                xs={{ span: 24 }}
                sm={{ span: 10 }}
                md={{ span: 8 }}
                lg={{ span: 8 }}
              >
                <Spin spinning={this.state.loading}>
                  <RangePicker
                    data-intercom-target="date-range-picker-analytics-trends"
                    style={{ width: '100%' }}
                    defaultValue={[
                      this.props.startDate
                        ? dayjs(this.props.startDate)
                        : undefined,
                      this.props.endDate
                        ? dayjs(this.props.endDate)
                        : undefined,
                    ]}
                    presets={[
                      {
                        label: 'Today',
                        value: [dayjs(), dayjs()],
                      },
                      {
                        label: 'This Month',
                        value: [
                          dayjs().startOf('month'),
                          dayjs().endOf('month'),
                        ],
                      },
                      {
                        label: 'Last Month',
                        value: [
                          dayjs().subtract(1, 'month').startOf('month'),
                          dayjs().subtract(1, 'month').endOf('month'),
                        ],
                      },
                      {
                        label: 'Year to Date',
                        value: [dayjs().startOf('year'), dayjs()],
                      },
                      {
                        label: 'Last Year',
                        value: [
                          dayjs().subtract(1, 'year').startOf('year'),
                          dayjs().subtract(1, 'year').endOf('year'),
                        ],
                      },
                    ]}
                    onChange={this.onChange.bind(this)}
                    disabledDate={disabledDate}
                    onCalendarChange={(dateArray) => {
                      const endDateSelected = dateArray[1] !== null;
                      if (endDateSelected) {
                        return this.setState({
                          date: undefined,
                        });
                      }

                      const startDateSelected = dateArray[0];
                      if (startDateSelected) {
                        this.setState({
                          date: dateArray[0],
                        });
                      }
                    }}
                  />
                </Spin>
              </Col>
              <Col
                xs={{ span: 24 }}
                sm={{ span: 6 }}
                md={{ span: 6 }}
                lg={{ span: 4 }}
              >
                {this.state.dataCSVToBeExported.length > 0 && (
                  <CSVLink
                    data={this.state.dataCSVToBeExported}
                    filename={`RichReturns-Trend-Overview-${this.state.timestamp}.csv`}
                    className="hidden"
                    ref={this.csvLinkRef}
                  />
                )}

                <Dropdown
                  overlay={
                    <Menu>
                      <Menu.Item
                        key="csv"
                        onClick={this.handleExportCsv.bind(this)}
                        disabled={this.state.exportLoading}
                      >
                        Export CSV
                      </Menu.Item>
                      <Menu.Item
                        key="pdf"
                        onClick={this.handleExportPdf}
                        disabled={this.state.pdfLoading}
                      >
                        Export PDF
                      </Menu.Item>
                    </Menu>
                  }
                  placement="bottomRight"
                >
                  <Button block={true}>Export</Button>
                </Dropdown>
              </Col>
            </Row>

            {/* <Divider style={{ marginTop: 30, marginBottom: 30 }}>
                  Select a start and end date
                </Divider> */}
            <div ref={this.printRef}>
              <Row
                type="flex"
                justify="center"
                align="middle"
                style={{ paddingTop: 20 }}
              >
                <Col span={24}>
                  <div style={{ overflowX: 'auto' }}>
                    <div style={{ minWidth: 768 }}>
                      <Area
                        data={this.state.data}
                        xField="date"
                        yField="volume"
                        seriesField={'type'}
                        shapeField="smooth"
                        // color={['#6897a7', '#8bc0d6', '#60d7a7', '#0000FF']}
                        colorField="type"
                        xAxis={{
                          type: 'time',
                          mask: 'YYYY-MM-DD',
                          tickCount: 5,
                        }}
                        yAxis={{
                          label: {
                            formatter: function formatter(v) {
                              return ''
                                .concat(v)
                                .replace(/\d{1,3}(?=(\d{3})+$)/g, function (s) {
                                  return ''.concat(s, ',');
                                });
                            },
                          },
                          tickInterval: 1,
                        }}
                        // legend={{ position: 'top' }}
                        legend={{ position: 'top' }}
                        meta={{
                          date: {
                            alias: 'Date',
                          },
                          volume: {
                            alias: 'Volume',
                            formatter: (v) => {
                              return ''
                                .concat(v)
                                .replace(/\d{1,3}(?=(\d{3})+$)/g, function (s) {
                                  return ''.concat(s, ',');
                                });
                            },
                          },
                        }}
                        onReady={(plot) => {
                          plot.on('legend-item:click', (ev) => {
                            const { checked } = ev.data;
                            if (!checked) {
                              return message.error(
                                'You need to upgrade to the Advanced plan to view this data.',
                                4
                              );
                            }
                          });
                        }}
                        onError={(err) => {
                          console.error('Error rendering Area chart:', err);
                          message.error(
                            'Error rendering Area chart. Please try again later.',
                            4
                          );
                        }}
                        style={{ height: 400, width: '100%' }}
                      />
                    </div>
                  </div>
                </Col>
              </Row>
            </div>
          </React.Fragment>
        ) : (
          this.context.featureAnalyticsAdvanced !== undefined && (
            <React.Fragment>
              <Row type="flex" justify="space-around" align="top">
                <Col span={24}>
                  <Divider />
                </Col>
                <Upgrade
                  title="Build advanced rules & scenarios for your returns."
                  description="Upgrade to a higher plan to tailor returns to your business processes and goals based on order data and customer actions."
                  faqLink="https://support.richreturns.io/en/articles/17693616-overview-of-advanced-rules?utm_source=merchant_panel"
                  videoId="DwAxUUS7A0o"
                />
              </Row>
            </React.Fragment>
          )
        )}
      </React.Fragment>
    );
  }
}

export default Overview;
